import React, { useEffect, useState } from "react";
import { Flex, Text, Button } from "@chakra-ui/react";
import { appointmentTypesStore } from "../../store/AppointmentTypesStore";
import { bookingFlowStore } from "../../store/BookingFlowStore";
import ServiceTypeSkeleton from "../ServiceTypeSkeleton";

interface Step4Props {
  appointmentTypes: any[];
  isLoading: boolean;
  error: any;
  onSelect: (appointmentType: any) => void;
  selectedDoctor?: any;
  userFk?: string;
  clinicGroupCrm?: string;
  locationId?: string;
}

const Step4_AppointmentType: React.FC<Step4Props> = ({ 
  appointmentTypes, 
  isLoading, 
  error, 
  onSelect,
  selectedDoctor,
  userFk,
  clinicGroupCrm,
  locationId
}) => {
  // State to hold filtered appointment types
  const [filteredAppointmentTypes, setFilteredAppointmentTypes] = useState<any[]>([]);
  // Filter appointment types based on selected doctor and additional criteria when component mounts or dependencies change
  useEffect(() => {
    if (appointmentTypes?.length > 0) {
      let filtered = [...appointmentTypes];
      
      // Apply filters only if a doctor is selected
      if (selectedDoctor) {
        // Filter by appointment type IDs if they exist
        if (selectedDoctor.appt_types) {
          const doctorAppointmentTypeIds = Array.isArray(selectedDoctor.appt_types)
            ? selectedDoctor.appt_types.map(String)
            : [String(selectedDoctor.appt_types)];
      
          filtered = filtered.filter(
            (apt) => doctorAppointmentTypeIds.includes(String(apt.appointment_type_id))
          );
        }
      
        const doctorUserFk = selectedDoctor.user_fk || userFk;
        const doctorClinicGroupCrm = selectedDoctor.clinic_group_crm || clinicGroupCrm;
        const doctorLocationId = selectedDoctor.location_id || locationId;
      
        if (doctorUserFk) {
          filtered = filtered.filter((apt) => apt.user_fk === doctorUserFk);
        }
        if (doctorClinicGroupCrm) {
          filtered = filtered.filter((apt) => apt.clinic_group_crm === doctorClinicGroupCrm);
        }
        if (doctorLocationId) {
          filtered = filtered.filter((apt) => apt.location_id === doctorLocationId);
        }
      } else {
        // If no doctor is selected, use the passed parameters
        if (userFk) {
          filtered = filtered.filter(apt => apt.user_fk === userFk);
        }
        
        if (clinicGroupCrm) {
          filtered = filtered.filter(apt => apt.clinic_group_crm === clinicGroupCrm);
        }
        
        if (locationId) {
          filtered = filtered.filter(apt => apt.location_id === locationId);
        }
      }
      
      setFilteredAppointmentTypes(filtered);
    } else {
      setFilteredAppointmentTypes([]);
    }
  }, [appointmentTypes, selectedDoctor, userFk, clinicGroupCrm, locationId]);

  const handleSelect = (appointmentType: any) => {
    if (appointmentTypesStore.selectedAptType !== appointmentType) {
      appointmentTypesStore.setSelectedAptType(appointmentType);
    }
    bookingFlowStore.updateAppointmentData("appointmentType", appointmentType);
    bookingFlowStore.goToStep(5);
    bookingFlowStore.setActiveStep(5);
  };

  // Add these handlers right before the return statement
  const handleServiceTypeClick = (e: React.MouseEvent) => {
    e.preventDefault();
    bookingFlowStore.goToStep(3);
    bookingFlowStore.setActiveStep(3);
  };

  const handleDoctorClick = (e: React.MouseEvent) => {
    e.preventDefault();
    bookingFlowStore.setIsModalOpen(false);
    bookingFlowStore.resetFlow();
  };

  return (
    <Flex flexDirection="column" alignItems="center" justifyContent="center" width="100%">
      <Text fontSize="lg" fontWeight="bold" mb={4}>Select your appointment type</Text>

      {isLoading ? (
        <ServiceTypeSkeleton/>
      ) : error ? (
        <p>Error loading appointment types: {error.message}</p>
      ) : filteredAppointmentTypes?.length === 0 ? (
        <Text color="orange.500">
          This Doctor doesn't currently offer appointments of the selected type at this clinic. Please choose a{' '}
          <Text
            as="span"
            color="blue.500"
            textDecoration="underline"
            cursor="pointer"
            onClick={handleServiceTypeClick}
          >
            different service type
          </Text>
          {' '}or{' '}
          <Text
            as="span"
            color="blue.500"
            textDecoration="underline"
            cursor="pointer"
            onClick={handleDoctorClick}
          >
            select another Doctor
          </Text>
        </Text>
      ) : (
        filteredAppointmentTypes?.map((appointmentType) => (
          <Button
            key={appointmentType.appointment_type_id}
            onClick={() => handleSelect(appointmentType)}
            mb={3}
            maxWidth="400px"
            minWidth="320px"
            boxShadow="md"
            border="2px"
            borderColor="#B92F2F"
            variant="outline"
            textAlign="center"
            whiteSpace="normal"
            overflowWrap="break-word"
            overflow="hidden"
            minHeight="60px"
            paddingY="2"
            _hover={{ bg: "#B92F2F", color: "#fff" }}
          >
            {appointmentType.display_name}
          </Button>
        ))
      )}
    </Flex>
  );
};

export default Step4_AppointmentType;